import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWatch } from 'react-hook-form';
import { observer } from 'mobx-react-lite';
import { cssVar, Button, IconButton, Spinner, Text, DataList, DataItem } from '@cad-arc';
import { EventPanelMetadata } from '@mark43/cad-api';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { FrPageHeader } from '../fr-page-layout/fr-page-header';
import { useEventMetadataStore } from '../event-panel/event-panel-form/event-metadata';
import { EntityStatusIcon } from '../entity-status-icon';
import { useCopyText } from '../common/hooks/use-copy-text';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import { FR_EDIT_EVENT_SUB_PATH, FR_EVENT_ROOT_PATH } from '../router/constants';
import { useUnitAssignment } from '../unit-assignment/unit-assignment-context';
import { useIntegratedForm } from '../forms/use-integrated-form';
import { FrEventUnitPills } from './fr-event-unit-pills';
import { FrEventFormType } from '../fr-entity-panels/types';
import { EventLabelsView } from '../event-labels-view';
import { HighlightBox } from '../common/components/highlighted-box';
import { useEventLocationNotifications } from '../common/hooks/use-event-location-notifications';

export const FrEventPageHeader = observer(function FrEventPageHeader({
    isClearEventPage = false,
    isUnassignCurrentUserUnitPage = false,
}: {
    isClearEventPage?: boolean;
    isUnassignCurrentUserUnitPage?: boolean;
} = {}) {
    const eventMetadataStore = useEventMetadataStore();
    const {
        agencyEventNumber,
        isActive,
        isScheduledEvent,
        eventId,
        callForServiceCode,
        callForServiceDescription,
        pillUnits,
    } = eventMetadataStore?.state?.metadata ?? ({} as EventPanelMetadata);

    const unitAssignment = useUnitAssignment();
    const { eventLabels: eventLabelsEnabled, editClearedEvents } = useEnabledFeatures();
    const navigate = useNavigate();
    const { form } = useIntegratedForm<FrEventFormType>();
    const copyText = useCopyText();
    const eventLocation = useWatch({ control: form.control, name: 'location' });
    const isEventLoading = !callForServiceCode || !callForServiceDescription;
    const isAssignedEvent = eventId ? eventId === unitAssignment?.assignedEvent?.eventId : false;
    const isClearedEvent = !!eventId && !isActive && !isScheduledEvent;
    const [firstRen] = eventMetadataStore?.eventRens ?? [];
    const renLabel = useBootstrapState()?.fieldDisplayNameByFieldName?.[
        'AGENCY_EVENT_REPORTING_EVENT_NUMBER'
    ];

    const pageTitleContent = `${callForServiceCode} - ${callForServiceDescription}`;
    const pageTitlePrefix = isClearEventPage
        ? 'Clear '
        : isUnassignCurrentUserUnitPage
        ? 'Unassign Myself From '
        : '';
    const pageTitle = `${pageTitlePrefix}${pageTitleContent}`;

    const shouldShowHeaderActions = !isClearEventPage && !isUnassignCurrentUserUnitPage;

    const goToEditEventPage = useCallback(() => {
        navigate(`${FR_EVENT_ROOT_PATH}/${eventId}/${FR_EDIT_EVENT_SUB_PATH}`);
    }, [navigate, eventId]);

    const assignedEventId = unitAssignment?.assignedEvent?.eventId;
    const {
        isLocationUpdated,
        removeEventLocationNotifications,
    } = useEventLocationNotifications(assignedEventId, { autoClear: true });

    return (
        <FrPageHeader
            title={isEventLoading ? null : pageTitle}
            showGoBackButton
            complementaryContent={
                eventId && (
                    <DataList
                        direction="row"
                        spacing={4}
                        style={{ fontSize: cssVar('arc.fontSizes.md'), alignItems: 'center' }}
                    >
                        {eventLocation?.displayLineFirst && (
                            <HighlightBox
                                highlight={isLocationUpdated}
                                onClick={removeEventLocationNotifications}
                            >
                                <Text fontWeight="bold">{eventLocation.displayLineFirst}</Text>
                            </HighlightBox>
                        )}
                        <DataItem dataKey="Event #">{agencyEventNumber}</DataItem>
                        <DataItem dataKey={renLabel ?? 'REN'}>
                            <Text>{firstRen ?? '-'}</Text>
                        </DataItem>
                        {firstRen && (
                            <IconButton
                                icon="Duplicate"
                                variant="ghost"
                                style={{ marginLeft: 5 }}
                                onClick={() =>
                                    copyText({
                                        text: firstRen,
                                        copiedFieldLabel: renLabel,
                                    })
                                }
                            />
                        )}
                        <FrEventUnitPills units={pillUnits} />
                    </DataList>
                )
            }
        >
            {isEventLoading ? (
                <Spinner size="md" />
            ) : (
                <>
                    <EntityStatusIcon
                        isActive={isActive}
                        isScheduledEvent={isScheduledEvent}
                        size="md"
                    />
                    {shouldShowHeaderActions && (
                        <>
                            {eventLabelsEnabled && (
                                <EventLabelsView
                                    labels={form.getValues('eventLabels')}
                                    truncateCount={1}
                                />
                            )}
                            {(isAssignedEvent || (isClearedEvent && editClearedEvents)) && (
                                <Button
                                    leadingVisual="Edit"
                                    style={{
                                        position: 'absolute',
                                        right: cssVar('arc.space.4'),
                                        top: cssVar('arc.space.4'),
                                    }}
                                    onClick={goToEditEventPage}
                                >
                                    Edit
                                </Button>
                            )}
                        </>
                    )}
                </>
            )}
        </FrPageHeader>
    );
});
